/**
 * 통합 SMS 관리 Composable
 *
 * 단순화된 SMS 전송 로직:
 * - 항상 1개의 메시지만 전송 (긴 메시지는 앱에서 자동 분할)
 * - phoneNumber + message만으로 전송
 */

import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth/useAuthStore'
import { sendSmsBatch, generateMessageId } from '@/services/sms/smsService'
import { getSmsCharacterCount } from '@/utils/message/messageUtils'
import type { SmsMessage } from '@farmfree/common/types/message/fcmMessage'

export function useUnifiedSms() {
  const authStore = useAuthStore()

  // SMS 상태 관리
  const newMessage = ref('')
  const sendingSms = ref(false)

  /**
   * SMS 전송 (단일 메시지)
   * @param phoneNumber 수신자 전화번호
   * @param message 메시지 내용
   */
  const sendSms = async (phoneNumber: string, message: string): Promise<void> => {
    const uid = authStore.currentUser?.uid

    if (!uid) {
      throw new Error('로그인이 필요합니다')
    }

    if (!phoneNumber?.trim()) {
      throw new Error('전화번호를 입력해주세요')
    }

    if (!message?.trim()) {
      throw new Error('메시지를 입력해주세요')
    }

    // 전화번호 정규화 (하이픈 제거, +82 제거 등)
    const normalizedPhone = phoneNumber.replace(/[-\s+]/g, '').replace(/^82/, '0')

    try {
      sendingSms.value = true

      // SMS 메시지 구성
      const smsMessage: SmsMessage = {
        phoneNumber: normalizedPhone,
        message: message.trim(),
        messageId: generateMessageId(),
      }

      // API 호출 (항상 1개 메시지만 전송)
      const response = await sendSmsBatch(uid, [smsMessage])

      if (!response.success) {
        throw new Error(response.message || 'SMS 발송 실패')
      }

      console.log('✅ SMS 발송 요청 완료')
    } catch (error) {
      console.error('❌ SMS 전송 실패:', error)
      throw error
    } finally {
      sendingSms.value = false
    }
  }

  // v1 호환성 함수 (v1 페이지 삭제 시 제거 예정)
  const handleMessageInput = (event: Event | string) => {
    if (typeof event === 'string') {
      newMessage.value = event
    } else {
      const target = event.target as HTMLTextAreaElement
      if (target) {
        newMessage.value = target.value
      }
    }
  }

  const sendSmsToOrderPen = async (orderPen: any) => {
    await sendSms(orderPen?.receiverPhone1 || '', newMessage.value)
  }

  const sendSmsToPhone = async (phoneNumber: string) => {
    await sendSms(phoneNumber, newMessage.value)
  }

  const sendMultipleSmsToOrderPen = async (orderPen: any, messages: string[]) => {
    // v1 호환: 첫 번째 메시지만 전송
    const message = messages[0] || newMessage.value
    await sendSms(orderPen?.receiverPhone1 || '', message)
  }

  return {
    // 상태
    newMessage,
    sendingSms,

    // 유틸리티
    getSmsCharacterCount,

    // v2 API (권장)
    sendSms,

    // v1 호환성 API (v1 페이지 삭제 시 제거 예정)
    handleMessageInput,
    sendSmsToOrderPen,
    sendSmsToPhone,
    sendMultipleSmsToOrderPen,
  }
}
